/**
 * Whether a product is organic (*biologisch*), as a first-class attribute.
 *
 * This is deliberately **not** part of `dutch.ts`. `fold` there refuses to make
 * `bio` equal `biologisch`, and rightly so: for deciding whether two titles are
 * the *same product*, they are two different words, and folding them together
 * would be a synonym table wearing a stemmer's clothes. The question here is a
 * different one — not "does this title match that title" but "is this product
 * certified organic" — and for *that*, `bio` and `biologisch` are two spellings
 * of one fact. Hence its own module, with its own tests.
 *
 * Three sources, in the order the feature names them:
 *
 *   - **metadata** — an explicit flag from the store's own payload, and the
 *     brand, which is where an organic house line announces itself (`Bio+`,
 *     `AH Biologisch`);
 *   - **the name** — `bio`/`biologisch` in the product title;
 *   - **a trusted-store default** — a shop whose entire range is certified, of
 *     which Ekoplaza is the one this app talks to.
 *
 * Deterministic and price-blind, like identity: it never sees a model and never
 * reads a price, so the same inputs always give the same answer and it can run
 * in the browser, in the adapter layer and in a SQL write path alike.
 */
/**
 * Shops whose entire assortment is certified organic, so every product from
 * them is organic regardless of what its own title happens to say.
 *
 * Ekoplaza is the Netherlands' organic supermarket chain (ADAPTERS §7). This is
 * the **single source of truth** for the store default — the DB write path
 * (`sync/apply.ts`), live-search presentation (`adapters/present.ts`) and the
 * client all read it — so there is no second list to drift out of step with a
 * `stores` column.
 */
export const ORGANIC_STORE_CODES = ['ekoplaza'];
export function isOrganicStore(store) {
    return store != null && ORGANIC_STORE_CODES.includes(store);
}
/**
 * Whether one word is an organic marker.
 *
 * Matched as a whole token rather than as a substring, and that is the whole
 * care of it: `bio` is organic, but `bioscoop` (cinema), `biotex` (a detergent)
 * and `antibiotica` are not, and a naive `includes('bio')` flags all three.
 * `biologisch` and its inflection `biologische` are caught by prefix, because
 * Dutch closes the adjective with a trailing `-e` and nothing else meaningful
 * follows the stem.
 */
function tokenIsOrganic(token) {
    return token === 'bio' || token === 'organic' || token.startsWith('biologisch');
}
/**
 * Whether any of the given texts name an organic product.
 *
 * Tokenises on non-letters, so `Bio+`, `bio-appel` and `AH Biologisch` all read
 * as organic while `Bioscoopkaartje` does not. Applied to name and brand
 * together — the brand is where `Bio+` and `AH Biologisch` live, which is the
 * "metadata" half of detection that does not need an explicit flag.
 */
export function nameSuggestsOrganic(...texts) {
    for (const text of texts) {
        if (!text)
            continue;
        for (const token of text.toLowerCase().split(/[^a-z]+/)) {
            if (token && tokenIsOrganic(token))
                return true;
        }
    }
    return false;
}
/**
 * The one place that decides. Any of the three signals is enough — organic is a
 * claim a single strong marker supports, unlike a bundle, which needs several
 * things to agree.
 */
export function detectOrganic(signals) {
    return (signals.flag === true ||
        isOrganicStore(signals.store) ||
        nameSuggestsOrganic(signals.name, signals.brand));
}
//# sourceMappingURL=organic.js.map